import {
  ANGULOS_DE_FOTO,
  ANGULOS_OBRIGATORIOS,
  type AnguloDeFoto,
  angulosFaltantes,
  avariaSchema,
  GRAVIDADES_DE_AVARIA,
  type GravidadeDeAvaria,
  type ItemDeChecklist,
  OITAVOS_DO_TANQUE,
  ROTULO_DA_GRAVIDADE,
  ROTULO_DO_ANGULO,
  ROTULO_DO_TIPO_DE_AVARIA,
  rotuloDoNivelDeCombustivel,
  TIPOS_DE_AVARIA,
  type TipoDeAvaria,
} from '@locaveri/shared'
import { CircleCheck, ImagePlus, LoaderCircle, Trash2, TriangleAlert, X } from 'lucide-react'
import {
  forwardRef,
  type ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useAssinarUpload, useChecklist } from '@/features/vistorias/api'
import {
  AssinaturaCanvas,
  type AssinaturaCanvasHandle,
} from '@/features/vistorias/assinatura-canvas'
import { BADGE_DA_GRAVIDADE, DiagramaDoCarro } from '@/features/vistorias/diagrama-do-carro'
import { errorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * O formulário de UMA vistoria — hodômetro, combustível em oitavos, checklist
 * do tenant, fotos por ângulo (upload assinado), avarias no diagrama,
 * assinatura em canvas e observações.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ELE É UM COMPONENTE, E NÃO UMA SEÇÃO DA PÁGINA
 * ---------------------------------------------------------------------------
 * A TROCA de veículo (Adendo C) registra DUAS vistorias numa operação só — o
 * retorno do carro que sai e a saída do que entra. Duplicar este formulário
 * seria o dia em que a vistoria da troca aceita o que a vistoria normal recusa.
 * Por isso ele é CONTROLADO: `valor` entra por prop e toda mudança sai por
 * `aoMudar`, que recebe um ATUALIZADOR (`atual => próximo`) — os uploads são
 * assíncronos, e um `onChange(valorNovo)` com o valor capturado no closure
 * perderia a foto que terminou de subir enquanto outra era escolhida.
 *
 * ---------------------------------------------------------------------------
 * A ASSINATURA É IMPERATIVA DE PROPÓSITO
 * ---------------------------------------------------------------------------
 * O traço vive no canvas, não no estado — serializá-lo a cada movimento seria
 * caro e inútil. Quem envia chama `obterAssinaturaChave()` no handle: o PNG é
 * exportado e sobe com CACHE até o traço mudar, então um submit reprovado no
 * Zod não reenvia o mesmo arquivo três vezes. `null` = ninguém assinou.
 *
 * O upload de cada foto anda na frente do submit (URL assinada + `PUT` direto
 * no storage); o formulário guarda só a CHAVE — o submit não espera dez
 * binários.
 */

// ---------------------------------------------------------------------------
// O valor controlado
// ---------------------------------------------------------------------------

export interface RespostaDigitada {
  readonly estado: 'OK' | 'PROBLEMA' | null
  readonly observacao: string
}

/** Uma foto já enviada ao storage, com a miniatura local para conferência. */
export interface FotoEnviada {
  readonly chaveLocal: string
  readonly angulo: AnguloDeFoto
  /** A chave no storage — o que a vistoria grava. */
  readonly arquivoChave: string
  readonly urlLocal: string
}

export interface AvariaMarcada {
  readonly chaveLocal: string
  readonly tipo: TipoDeAvaria
  readonly gravidade: GravidadeDeAvaria
  readonly posicaoXEmMilesimos: number
  readonly posicaoYEmMilesimos: number
  readonly descricao: string
}

export interface ValorDaVistoria {
  readonly hodometro: string
  readonly combustivel: number | null
  readonly respostas: Readonly<Record<string, RespostaDigitada>>
  readonly fotos: readonly FotoEnviada[]
  /** Ângulos com upload em andamento — o submit espera esvaziar. */
  readonly enviandoAngulos: readonly AnguloDeFoto[]
  readonly avarias: readonly AvariaMarcada[]
  readonly observacoes: string
}

export const VALOR_INICIAL_DA_VISTORIA: ValorDaVistoria = {
  hodometro: '',
  combustivel: null,
  respostas: {},
  fotos: [],
  enviandoAngulos: [],
  avarias: [],
  observacoes: '',
}

/** Mudanças saem SEMPRE como atualizador — ver o cabeçalho do arquivo. */
export type AoMudarVistoria = (atualizar: (atual: ValorDaVistoria) => ValorDaVistoria) => void

// ---------------------------------------------------------------------------
// Corpo para o schema compartilhado
// ---------------------------------------------------------------------------

/**
 * O corpo que `vistoriaAvulsaSchema` (e, com `tipo`, `registrarVistoriaSchema`)
 * valida. Hodômetro em branco vira `undefined` — string vazia no
 * `z.coerce.number` viraria "0 km" válido em vez de erro.
 */
export function corpoDaVistoria(
  valor: ValorDaVistoria,
  assinaturaArquivoChave: string,
): Record<string, unknown> {
  const respostas = Object.entries(valor.respostas).flatMap(([chave, resposta]) =>
    resposta.estado === null
      ? []
      : [
          {
            chave,
            ok: resposta.estado === 'OK',
            ...(resposta.observacao.trim().length === 0
              ? {}
              : { observacao: resposta.observacao.trim() }),
          },
        ],
  )

  return {
    hodometro: valor.hodometro.trim().length === 0 ? undefined : valor.hodometro,
    combustivelEmOitavos: valor.combustivel ?? undefined,
    respostas,
    fotos: valor.fotos.map((foto) => ({ angulo: foto.angulo, arquivoChave: foto.arquivoChave })),
    avarias: valor.avarias.map((avaria) => ({
      tipo: avaria.tipo,
      gravidade: avaria.gravidade,
      posicaoXEmMilesimos: avaria.posicaoXEmMilesimos,
      posicaoYEmMilesimos: avaria.posicaoYEmMilesimos,
      descricao: avaria.descricao,
    })),
    assinaturaArquivoChave,
    ...(valor.observacoes.trim().length === 0 ? {} : { observacoes: valor.observacoes.trim() }),
  }
}

// ---------------------------------------------------------------------------
// Distribuição dos erros do Zod pelas seções
// ---------------------------------------------------------------------------

/**
 * As seções do formulário que podem receber erro. O formulário não usa
 * react-hook-form (fotos, avarias e assinatura não são campos de texto), então
 * a distribuição é feita à mão sobre o `safeParse`, com a seção como unidade.
 */
export const CAMPOS_DA_VISTORIA = [
  'hodometro',
  'combustivelEmOitavos',
  'respostas',
  'fotos',
  'avarias',
  'assinaturaArquivoChave',
  'observacoes',
] as const

export type CampoDaVistoria = (typeof CAMPOS_DA_VISTORIA)[number]
export type ErrosDaVistoria = Partial<Record<CampoDaVistoria, string>>

/** O mínimo de um issue do Zod — estrutural, para não amarrar versão. */
export interface IssueDistribuivel {
  readonly path: readonly PropertyKey[]
  readonly message: string
}

/**
 * Espalha os issues pelas seções. Erro dentro de um item (`avarias.1.descricao`)
 * sobe para a seção com o índice humano na frente — é na seção que a pessoa
 * corrige. O que não pertence a seção nenhuma (o `tipo` do registro, refines de
 * conjunto sem path conhecido) volta em `sobras` para a tela exibir onde couber.
 */
export function distribuirErrosDaVistoria(issues: readonly IssueDistribuivel[]): {
  readonly porCampo: ErrosDaVistoria
  readonly sobras: readonly string[]
} {
  const porCampo: ErrosDaVistoria = {}
  const sobras: string[] = []

  for (const issue of issues) {
    const raiz = issue.path[0]
    const campo = CAMPOS_DA_VISTORIA.find((candidato) => candidato === raiz)

    const mensagem =
      issue.path.length > 1 && typeof issue.path[1] === 'number'
        ? `Item ${String(issue.path[1] + 1)}: ${issue.message}`
        : issue.message

    if (campo === undefined) {
      sobras.push(issue.message)
      continue
    }

    const atual = porCampo[campo]
    porCampo[campo] = atual === undefined ? mensagem : `${atual} · ${mensagem}`
  }

  return { porCampo, sobras }
}

// ---------------------------------------------------------------------------
// Peças pequenas
// ---------------------------------------------------------------------------

/**
 * Gravidade nunca é só cor (regra do CLAUDE.md): a legenda amarra a cor do
 * marcador ao rótulo, e aparece ao lado de todo diagrama que tem marcador.
 */
export function LegendaDeGravidade(): ReactElement {
  return (
    <ul className="flex flex-wrap items-center gap-1.5" aria-label="Legenda de gravidade">
      {GRAVIDADES_DE_AVARIA.map((gravidade) => (
        <li key={gravidade}>
          <Badge variant={BADGE_DA_GRAVIDADE[gravidade]}>{ROTULO_DA_GRAVIDADE[gravidade]}</Badge>
        </li>
      ))}
    </ul>
  )
}

const NIVEIS_DE_COMBUSTIVEL = Array.from({ length: OITAVOS_DO_TANQUE + 1 }, (_, indice) => indice)

interface CartaoDeFotoProps {
  readonly angulo: AnguloDeFoto
  readonly fotos: readonly FotoEnviada[]
  /** Ângulo obrigatório ainda sem foto — é o contorno de atenção. */
  readonly faltando: boolean
  readonly enviando: boolean
  readonly desabilitado: boolean
  readonly aoEscolher: (arquivos: FileList | null) => void
  readonly aoRemover: (chaveLocal: string) => void
}

/**
 * Um cartão por ângulo. O contorno âmbar + o selo "Falta foto" marcam o
 * obrigatório pendente (cor E rótulo); `DETALHE` aceita várias fotos porque é
 * o ângulo livre de aproximação — os demais trocam a foto em vez de acumular.
 */
function CartaoDeFoto({
  angulo,
  fotos,
  faltando,
  enviando,
  desabilitado,
  aoEscolher,
  aoRemover,
}: CartaoDeFotoProps): ReactElement {
  const obrigatorio = ANGULOS_OBRIGATORIOS.includes(angulo)

  return (
    <div
      className={cn(
        'space-y-2 rounded-lg border p-3',
        faltando ? 'border-warning-fg/50 bg-warning/5' : 'border-border bg-surface',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{ROTULO_DO_ANGULO[angulo]}</p>
        {faltando ? (
          <Badge variant="warning">
            <TriangleAlert aria-hidden="true" />
            Falta foto
          </Badge>
        ) : obrigatorio ? (
          <Badge variant="outline">Obrigatória</Badge>
        ) : null}
      </div>

      {fotos.length === 0 ? null : (
        <ul className="grid grid-cols-3 gap-2">
          {fotos.map((foto) => (
            <li key={foto.chaveLocal} className="relative">
              <img
                src={foto.urlLocal}
                alt={`Foto do ângulo ${ROTULO_DO_ANGULO[angulo]}`}
                className="h-16 w-full rounded-md border border-border object-cover"
              />
              <button
                type="button"
                aria-label={`Remover foto de ${ROTULO_DO_ANGULO[angulo]}`}
                onClick={() => {
                  aoRemover(foto.chaveLocal)
                }}
                className="absolute -right-1.5 -top-1.5 rounded-full border border-border
                  bg-elevated p-0.5 text-fg-secondary transition-colors hover:text-foreground"
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <label
        className={cn(
          'flex h-9 w-fit cursor-pointer items-center gap-2 rounded-lg border border-border',
          'bg-elevated px-3 text-sm font-medium text-foreground transition-colors',
          'focus-within:ring-2 focus-within:ring-ring hover:bg-accent',
          (enviando || desabilitado) && 'pointer-events-none opacity-50',
        )}
      >
        {enviando ? (
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
        ) : (
          <ImagePlus className="size-4" aria-hidden="true" />
        )}
        {fotos.length === 0
          ? 'Enviar foto'
          : angulo === 'DETALHE'
            ? 'Adicionar outra'
            : 'Trocar foto'}
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          multiple={angulo === 'DETALHE'}
          disabled={enviando || desabilitado}
          onChange={(evento) => {
            aoEscolher(evento.target.files)
            // Sem zerar, escolher o MESMO arquivo de novo não dispara change.
            evento.target.value = ''
          }}
        />
      </label>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Diálogo de avaria
// ---------------------------------------------------------------------------

interface PosicaoMarcada {
  readonly x: number
  readonly y: number
}

interface NovaAvaria {
  readonly tipo: TipoDeAvaria
  readonly gravidade: GravidadeDeAvaria
  readonly posicaoXEmMilesimos: number
  readonly posicaoYEmMilesimos: number
  readonly descricao: string
}

/** `<select>` devolve string; a lista fechada do domínio é quem a estreita. */
function tipoDeAvariaDoValor(valor: string): TipoDeAvaria {
  return TIPOS_DE_AVARIA.find((tipo) => tipo === valor) ?? 'OUTRO'
}

function gravidadeDoValor(valor: string): GravidadeDeAvaria {
  return GRAVIDADES_DE_AVARIA.find((gravidade) => gravidade === valor) ?? 'LEVE'
}

interface DialogoDeAvariaProps {
  /** A posição clicada no diagrama. `null` = diálogo fechado. */
  readonly posicao: PosicaoMarcada | null
  readonly aoFechar: () => void
  readonly aoConfirmar: (avaria: NovaAvaria) => void
}

/**
 * O mini-formulário de cada clique no diagrama. Valida com o `avariaSchema`
 * compartilhado antes de aceitar — "risco" sozinho não se defende depois, e é
 * o schema quem carrega essa frase. Tipo e gravidade vêm de listas fechadas,
 * então o único erro possível na prática é o da descrição.
 */
function DialogoDeAvaria({ posicao, aoFechar, aoConfirmar }: DialogoDeAvariaProps): ReactElement {
  const [tipo, setTipo] = useState<TipoDeAvaria>('RISCO')
  const [gravidade, setGravidade] = useState<GravidadeDeAvaria>('LEVE')
  const [descricao, setDescricao] = useState('')
  const [erroDaDescricao, setErroDaDescricao] = useState<string | null>(null)

  const aberto = posicao !== null

  // O diálogo é montado uma vez e reaberto a cada clique: sem o reset, a
  // segunda avaria nasceria com a descrição da primeira.
  useEffect(() => {
    if (aberto) {
      setTipo('RISCO')
      setGravidade('LEVE')
      setDescricao('')
      setErroDaDescricao(null)
    }
  }, [aberto])

  function confirmar(): void {
    if (posicao === null) {
      return
    }

    const validado = avariaSchema.safeParse({
      tipo,
      gravidade,
      posicaoXEmMilesimos: posicao.x,
      posicaoYEmMilesimos: posicao.y,
      descricao,
    })

    if (!validado.success) {
      setErroDaDescricao(validado.error.issues[0]?.message ?? 'avaria inválida')
      return
    }

    aoConfirmar({
      tipo: validado.data.tipo,
      gravidade: validado.data.gravidade,
      posicaoXEmMilesimos: validado.data.posicaoXEmMilesimos,
      posicaoYEmMilesimos: validado.data.posicaoYEmMilesimos,
      descricao: validado.data.descricao,
    })
  }

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Marcar avaria</DialogTitle>
          <DialogDescription>
            {posicao === null
              ? ''
              : `Posição ${String(posicao.x)} × ${String(posicao.y)} em milésimos do diagrama — o mesmo ponto em qualquer tela.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Tipo" obrigatorio>
              {(campo) => (
                <Select
                  {...campo}
                  value={tipo}
                  onChange={(evento) => {
                    setTipo(tipoDeAvariaDoValor(evento.target.value))
                  }}
                >
                  {TIPOS_DE_AVARIA.map((opcao) => (
                    <option key={opcao} value={opcao}>
                      {ROTULO_DO_TIPO_DE_AVARIA[opcao]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            <Campo rotulo="Gravidade" obrigatorio>
              {(campo) => (
                <Select
                  {...campo}
                  value={gravidade}
                  onChange={(evento) => {
                    setGravidade(gravidadeDoValor(evento.target.value))
                  }}
                >
                  {GRAVIDADES_DE_AVARIA.map((opcao) => (
                    <option key={opcao} value={opcao}>
                      {ROTULO_DA_GRAVIDADE[opcao]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
          </div>

          <Campo
            rotulo="Descrição"
            obrigatorio
            erro={erroDaDescricao ?? undefined}
            dica="Onde e o quê — é este texto que sustenta a cobrança meses depois."
          >
            {(campo) => (
              <Textarea
                {...campo}
                value={descricao}
                onChange={(evento) => {
                  setDescricao(evento.target.value)
                }}
                placeholder="Ex.: risco de uns 10 cm na porta dianteira esquerda, na altura da maçaneta"
              />
            )}
          </Campo>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="button" onClick={confirmar}>
            Adicionar avaria
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// O formulário
// ---------------------------------------------------------------------------

export interface FormularioDeVistoriaHandle {
  /**
   * Exporta o traço do canvas e o sobe ao storage (com cache até o traço
   * mudar), devolvendo a CHAVE para `assinaturaArquivoChave`. `null` quando
   * ninguém assinou; lança quando o upload falha — quem chama mostra o toast.
   */
  readonly obterAssinaturaChave: () => Promise<string | null>
  /** Limpa o canvas e o cache da assinatura. O `valor` é do pai — ele reseta. */
  readonly limpar: () => void
}

export interface FormularioDeVistoriaProps {
  /** "Vistoria de devolução — ABC1D23": o título diz de QUAL carro se trata. */
  readonly titulo: string
  readonly descricao?: string | undefined
  readonly valor: ValorDaVistoria
  readonly aoMudar: AoMudarVistoria
  readonly erros?: ErrosDaVistoria | undefined
  readonly desabilitado?: boolean
}

export const FormularioDeVistoria = forwardRef<
  FormularioDeVistoriaHandle,
  FormularioDeVistoriaProps
>(function FormularioDeVistoria(
  { titulo, descricao, valor, aoMudar, erros = {}, desabilitado = false },
  ref,
): ReactElement {
  const checklist = useChecklist()
  const assinarFoto = useAssinarUpload('FOTO_DE_VISTORIA')
  const assinarAssinatura = useAssinarUpload('ASSINATURA_DE_VISTORIA')
  const { mutateAsync: enviarFoto } = assinarFoto
  const { mutateAsync: enviarAssinatura } = assinarAssinatura

  const [posicaoDaAvaria, setPosicaoDaAvaria] = useState<PosicaoMarcada | null>(null)

  const assinaturaRef = useRef<AssinaturaCanvasHandle | null>(null)
  /** Chave da assinatura já no storage — o cache até o traço mudar. */
  const chaveDaAssinaturaRef = useRef<string | null>(null)

  // As miniaturas usam objectURL do arquivo local (o storage não serve GET
  // anônimo). O array é mutado por push e revogado inteiro no desmonte —
  // revogar a cada troca economizaria bytes e custaria um bug de miniatura.
  const urlsLocais = useRef<string[]>([])
  useEffect(() => {
    const urls = urlsLocais.current

    return () => {
      for (const url of urls) {
        URL.revokeObjectURL(url)
      }
    }
  }, [])

  const itensDoChecklist = [...(checklist.data?.itens ?? [])].sort((umItem, outroItem) =>
    umItem.ordemDeExibicao === outroItem.ordemDeExibicao
      ? umItem.rotulo.localeCompare(outroItem.rotulo)
      : umItem.ordemDeExibicao - outroItem.ordemDeExibicao,
  )

  const faltantes = angulosFaltantes(valor.fotos)

  const aoMudarAssinatura = useCallback((): void => {
    // Qualquer mudança no traço (novo risco OU limpar) invalida o PNG que já
    // subiu — senão um submit falho + rubrica extra enviaria a assinatura
    // antiga como se fosse a atual.
    chaveDaAssinaturaRef.current = null
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      obterAssinaturaChave: async () => {
        if (chaveDaAssinaturaRef.current !== null) {
          return chaveDaAssinaturaRef.current
        }

        const png = (await assinaturaRef.current?.exportarPng()) ?? null

        if (png === null) {
          return null
        }

        const envio = await enviarAssinatura(png)
        chaveDaAssinaturaRef.current = envio.chave
        return envio.chave
      },
      limpar: () => {
        assinaturaRef.current?.limpar()
        chaveDaAssinaturaRef.current = null
      },
    }),
    [enviarAssinatura],
  )

  function alternarResposta(chave: string, estado: 'OK' | 'PROBLEMA'): void {
    aoMudar((atual) => {
      const resposta = atual.respostas[chave] ?? { estado: null, observacao: '' }

      // Clicar de novo no estado já escolhido LIMPA a resposta: item opcional
      // pode ficar sem conferir, e "OK por engano" precisa de saída.
      return {
        ...atual,
        respostas: {
          ...atual.respostas,
          [chave]: { ...resposta, estado: resposta.estado === estado ? null : estado },
        },
      }
    })
  }

  function anotarObservacao(chave: string, observacao: string): void {
    aoMudar((atual) => {
      const resposta = atual.respostas[chave] ?? { estado: null, observacao: '' }

      return { ...atual, respostas: { ...atual.respostas, [chave]: { ...resposta, observacao } } }
    })
  }

  function aoEscolherFotos(angulo: AnguloDeFoto, lista: FileList | null): void {
    if (lista === null || lista.length === 0) {
      return
    }

    const arquivos = Array.from(lista)
    aoMudar((atual) => ({ ...atual, enviandoAngulos: [...atual.enviandoAngulos, angulo] }))

    void (async () => {
      try {
        for (const arquivo of arquivos) {
          const envio = await enviarFoto(arquivo)
          const urlLocal = URL.createObjectURL(arquivo)
          urlsLocais.current.push(urlLocal)

          aoMudar((atual) => {
            // DETALHE acumula; os demais ângulos TROCAM — duas "frentes" na
            // mesma vistoria seria ruído, não prova.
            const base =
              angulo === 'DETALHE'
                ? atual.fotos
                : atual.fotos.filter((foto) => foto.angulo !== angulo)

            return {
              ...atual,
              fotos: [
                ...base,
                { chaveLocal: envio.chave, angulo, arquivoChave: envio.chave, urlLocal },
              ],
            }
          })
        }
      } catch (causa) {
        toast.error(`Não foi possível enviar a foto (${ROTULO_DO_ANGULO[angulo]})`, {
          description: errorMessage(causa),
        })
      } finally {
        aoMudar((atual) => ({
          ...atual,
          enviandoAngulos: atual.enviandoAngulos.filter((pendente) => pendente !== angulo),
        }))
      }
    })()
  }

  function removerFoto(chaveLocal: string): void {
    aoMudar((atual) => ({
      ...atual,
      fotos: atual.fotos.filter((foto) => foto.chaveLocal !== chaveLocal),
    }))
  }

  function adicionarAvaria(nova: NovaAvaria): void {
    aoMudar((atual) => ({
      ...atual,
      avarias: [...atual.avarias, { ...nova, chaveLocal: crypto.randomUUID() }],
    }))
    setPosicaoDaAvaria(null)
  }

  function removerAvaria(chaveLocal: string): void {
    aoMudar((atual) => ({
      ...atual,
      avarias: atual.avarias.filter((avaria) => avaria.chaveLocal !== chaveLocal),
    }))
  }

  return (
    <div className="space-y-6">
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-foreground">{titulo}</h3>
        {descricao === undefined ? null : (
          <p className="max-w-prose text-sm text-fg-secondary">{descricao}</p>
        )}
      </div>

      {/* ------------------------------------------------------------
          Hodômetro e combustível
          ------------------------------------------------------------ */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="Hodômetro"
          obrigatorio
          erro={erros.hodometro}
          dica="Em quilômetros, o número do painel — a foto do PAINEL é a prova dele."
        >
          {(campo) => (
            <Input
              {...campo}
              type="number"
              min={0}
              inputMode="numeric"
              className="tabular-money"
              value={valor.hodometro}
              onChange={(evento) => {
                const hodometro = evento.target.value
                aoMudar((atual) => ({ ...atual, hodometro }))
              }}
              disabled={desabilitado}
            />
          )}
        </Campo>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            Nível de combustível
            <span aria-hidden="true" className="ml-1 text-fg-muted">
              *
            </span>
          </p>
          <div
            role="group"
            aria-label="Nível de combustível, em oitavos do tanque"
            className="flex items-stretch gap-1"
          >
            {NIVEIS_DE_COMBUSTIVEL.map((nivel) => (
              <button
                key={nivel}
                type="button"
                aria-pressed={valor.combustivel === nivel}
                aria-label={rotuloDoNivelDeCombustivel(nivel)}
                title={rotuloDoNivelDeCombustivel(nivel)}
                onClick={() => {
                  aoMudar((atual) => ({ ...atual, combustivel: nivel }))
                }}
                disabled={desabilitado}
                className={cn(
                  'h-8 min-w-0 flex-1 rounded-sm border transition-colors',
                  valor.combustivel !== null && nivel <= valor.combustivel
                    ? 'border-border-strong bg-fg-secondary'
                    : 'border-border bg-elevated hover:bg-accent',
                  valor.combustivel === nivel && 'ring-2 ring-ring',
                )}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-fg-muted">
            <span>Vazio</span>
            <span>Cheio</span>
          </div>
          <p className="text-xs text-fg-secondary">
            Ponteiro:{' '}
            <span className="font-medium text-foreground">
              {valor.combustivel === null
                ? 'não informado'
                : rotuloDoNivelDeCombustivel(valor.combustivel)}
            </span>
          </p>
          {erros.combustivelEmOitavos === undefined ? null : (
            <p className="text-xs text-danger-fg">{erros.combustivelEmOitavos}</p>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------
          Checklist do tenant
          ------------------------------------------------------------ */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">Checklist</h4>

        {checklist.isPending ? (
          <LoadingState linhas={2} rotulo="Carregando o checklist…" />
        ) : checklist.isError ? (
          <ErrorState
            titulo="Não foi possível carregar o checklist"
            erro={checklist.error}
            aoTentarNovamente={() => {
              void checklist.refetch()
            }}
          />
        ) : itensDoChecklist.length === 0 ? (
          <p className="text-sm text-fg-muted">
            Nenhum item configurado para esta locadora — a vistoria fecha só com fotos, avarias e
            assinatura.
          </p>
        ) : (
          <ul className="space-y-2">
            {itensDoChecklist.map((item: ItemDeChecklist) => {
              const resposta = valor.respostas[item.chave] ?? { estado: null, observacao: '' }

              return (
                <li
                  key={item.chave}
                  className="space-y-2 rounded-lg border border-border bg-surface p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="min-w-0 text-sm font-medium text-foreground">
                      {item.rotulo}
                      {item.obrigatorio ? (
                        <Badge variant="outline" className="ml-2 align-middle">
                          Obrigatório
                        </Badge>
                      ) : null}
                    </p>

                    <div className="flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-pressed={resposta.estado === 'OK'}
                        onClick={() => {
                          alternarResposta(item.chave, 'OK')
                        }}
                        disabled={desabilitado}
                        className={cn(
                          resposta.estado === 'OK' &&
                            'border-success-fg/40 bg-success/10 text-success-fg',
                        )}
                      >
                        <CircleCheck aria-hidden="true" />
                        OK
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-pressed={resposta.estado === 'PROBLEMA'}
                        onClick={() => {
                          alternarResposta(item.chave, 'PROBLEMA')
                        }}
                        disabled={desabilitado}
                        className={cn(
                          resposta.estado === 'PROBLEMA' &&
                            'border-danger-fg/40 bg-danger/10 text-danger-fg',
                        )}
                      >
                        <TriangleAlert aria-hidden="true" />
                        Problema
                      </Button>
                    </div>
                  </div>

                  {resposta.estado === 'PROBLEMA' ? (
                    <Input
                      aria-label={`Observação sobre ${item.rotulo}`}
                      placeholder="O que está errado? (vai para o registro)"
                      value={resposta.observacao}
                      onChange={(evento) => {
                        anotarObservacao(item.chave, evento.target.value)
                      }}
                      disabled={desabilitado}
                    />
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}

        {erros.respostas === undefined ? null : (
          <p className="text-xs text-danger-fg">{erros.respostas}</p>
        )}
      </section>

      {/* ------------------------------------------------------------
          Fotos por ângulo
          ------------------------------------------------------------ */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-foreground">Fotos por ângulo</h4>
          {faltantes.length === 0 ? (
            <Badge variant="success">
              <CircleCheck aria-hidden="true" />
              Ângulos obrigatórios completos
            </Badge>
          ) : (
            <Badge variant="warning">
              <TriangleAlert aria-hidden="true" />
              {faltantes.length === 1
                ? 'Falta 1 ângulo obrigatório'
                : `Faltam ${String(faltantes.length)} ângulos obrigatórios`}
            </Badge>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {ANGULOS_DE_FOTO.map((angulo) => (
            <CartaoDeFoto
              key={angulo}
              angulo={angulo}
              fotos={valor.fotos.filter((foto) => foto.angulo === angulo)}
              faltando={faltantes.includes(angulo)}
              enviando={valor.enviandoAngulos.includes(angulo)}
              desabilitado={desabilitado}
              aoEscolher={(arquivos) => {
                aoEscolherFotos(angulo, arquivos)
              }}
              aoRemover={removerFoto}
            />
          ))}
        </div>

        {erros.fotos === undefined ? null : <p className="text-xs text-danger-fg">{erros.fotos}</p>}
      </section>

      {/* ------------------------------------------------------------
          Avarias no diagrama
          ------------------------------------------------------------ */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-foreground">Avarias</h4>
          <LegendaDeGravidade />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <DiagramaDoCarro
            avarias={valor.avarias}
            aoMarcar={(x, y) => {
              setPosicaoDaAvaria({ x, y })
            }}
            rotulo="Diagrama do carro para marcar avarias"
            className="max-w-md"
          />

          <div className="min-w-0 space-y-2">
            {valor.avarias.length === 0 ? (
              <p className="text-sm text-fg-muted">
                Nenhuma avaria marcada. Clique no ponto da carroceria, no diagrama ao lado, para
                apontar uma — cada marcação pede tipo, gravidade e descrição.
              </p>
            ) : (
              <ol className="space-y-2">
                {valor.avarias.map((avaria, indice) => (
                  <li
                    key={avaria.chaveLocal}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg
                      border border-border bg-surface p-3"
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <span className="tabular-money text-sm font-semibold text-foreground">
                        {String(indice + 1)}.
                      </span>
                      <Badge variant={BADGE_DA_GRAVIDADE[avaria.gravidade]}>
                        {ROTULO_DA_GRAVIDADE[avaria.gravidade]}
                      </Badge>
                      <span className="text-sm font-medium text-foreground">
                        {ROTULO_DO_TIPO_DE_AVARIA[avaria.tipo]}
                      </span>
                      <span className="min-w-0 text-sm text-fg-secondary">{avaria.descricao}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        removerAvaria(avaria.chaveLocal)
                      }}
                      disabled={desabilitado}
                    >
                      <Trash2 aria-hidden="true" />
                      Remover
                    </Button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {erros.avarias === undefined ? null : (
          <p className="text-xs text-danger-fg">{erros.avarias}</p>
        )}
      </section>

      {/* ------------------------------------------------------------
          Assinatura e observações
          ------------------------------------------------------------ */}
      <section className="space-y-2">
        <h4 className="text-sm font-semibold text-foreground">
          Assinatura do cliente
          <span aria-hidden="true" className="ml-1 text-fg-muted">
            *
          </span>
        </h4>
        <div className="max-w-xl">
          <AssinaturaCanvas
            ref={assinaturaRef}
            aoMudar={aoMudarAssinatura}
            desabilitado={desabilitado}
          />
        </div>
        {erros.assinaturaArquivoChave === undefined ? null : (
          <p className="text-xs text-danger-fg">{erros.assinaturaArquivoChave}</p>
        )}
      </section>

      <Campo
        rotulo="Observações gerais"
        erro={erros.observacoes}
        dica="O que não coube em item nenhum — condições do pátio, relato do cliente."
      >
        {(campo) => (
          <Textarea
            {...campo}
            value={valor.observacoes}
            onChange={(evento) => {
              const observacoes = evento.target.value
              aoMudar((atual) => ({ ...atual, observacoes }))
            }}
            disabled={desabilitado}
          />
        )}
      </Campo>

      <DialogoDeAvaria
        posicao={posicaoDaAvaria}
        aoFechar={() => {
          setPosicaoDaAvaria(null)
        }}
        aoConfirmar={adicionarAvaria}
      />
    </div>
  )
})
